import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    ViewChild,
    forwardRef,
    inject,
} from '@angular/core';
import {
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    Validator,
} from '@angular/forms';
import {
    WdxButtonModule,
    WdxButtonSize,
    WdxButtonStyle,
} from '@wdx/shared/components/wdx-button';
import { WdxIconModule } from '@wdx/shared/components/wdx-icon';
import { WdxIconContainerModule } from '@wdx/shared/components/wdx-icon-container';
import { Subject } from 'rxjs/internal/Subject';

// https://github.com/josdejong/svelte-jsoneditor
import {
    Content,
    JSONContent,
    JSONEditor,
    Mode,
    createAjvValidator,
} from 'vanilla-jsoneditor';
import { jsonSerialiser } from './wdx-json-editor.utils';

export type CustomOutputHandler = (value?: Content) => any;
export type CustomInputHandler = (value?: Content) => JSONContent | undefined;

/**
 * For more details on the json editor visit
 * https://github.com/josdejong/svelte-jsoneditor
 */
@Component({
    selector: 'wdx-json-editor',
    templateUrl: './wdx-json-editor.component.html',
    styleUrls: ['./wdx-json-editor.component.scss'],
    imports: [WdxButtonModule, WdxIconModule, WdxIconContainerModule],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => WdxJsonViewerComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => WdxJsonViewerComponent),
            multi: true,
        },
    ],
})
export class WdxJsonViewerComponent
    implements AfterViewInit, ControlValueAccessor, Validator
{
    touched = false;
    disabled = false;
    errors: any;
    WDX_BUTTON_SIZE = WdxButtonSize;
    WDX_BUTTON_STYLE = WdxButtonStyle;
    @Input() schema: { [key: string | number]: any } | undefined;
    @Input() customInputHandler?: CustomInputHandler;
    @Input() customOutputHandler: CustomOutputHandler = jsonSerialiser;
    @Input() readonly = false;

    @Output() blurred = new EventEmitter();

    isFullscreen$ = new Subject<boolean>();
    private changeDetectorRef = inject(ChangeDetectorRef);

    @ViewChild('jsonEditorContainer', { static: true })
    jsonEditorContainer!: ElementRef;
    @ViewChild('jsonEditorWrapper', { static: true })
    jsonEditorWrapper!: ElementRef;
    editor!: JSONEditor;

    private data: JSONContent | undefined = undefined;

    ngAfterViewInit(): void {
        let validator;
        if (this.schema) {
            validator = createAjvValidator({
                schema: this.schema,
            });
        }

        this.editor = new JSONEditor({
            target: this.jsonEditorContainer.nativeElement,
            props: {
                content: this.customInputHandler
                    ? this.customInputHandler(this.data)
                    : this.data,
                mainMenuBar: true,
                navigationBar: true,
                mode: Mode.text,
                readOnly: this.readonly,
                onChange: (updatedContent, _, { contentErrors }) => {
                    this.errors = contentErrors;

                    this.onUserInput(updatedContent);

                    this.changeDetectorRef.detectChanges();
                },
                onBlur: () => {
                    this.blurred.emit();
                },
                validator,
            },
        });
    }

    onChange: (...args: any[]) => any = () => undefined;
    onTouched: (...args: any[]) => any = () => undefined;

    registerOnChange(onChange: (...args: any[]) => any): void {
        this.onChange = onChange;
    }

    registerOnTouched(onTouched: (...args: any[]) => any): void {
        this.onTouched = onTouched;
    }

    onUserInput(value: any): void {
        this.onChange(this.customOutputHandler(value));
    }

    writeValue(value: any): void {
        this.data = value
            ? {
                  json: value,
                  text: undefined,
              }
            : undefined;
        const json = this.customInputHandler
            ? (this.customInputHandler(this.data) as JSONContent)
            : (this.data as Content);
        this.editor?.update(json);
    }

    markAsTouched() {
        if (!this.touched) {
            this.onTouched();
            this.touched = true;
        }
    }

    setDisabledState(disabled: boolean) {
        this.disabled = disabled;
        this.editor?.updateProps({ readOnly: this.disabled });
    }

    validate(): { [key: string]: any } | null {
        if (this.errors) {
            return { invalidJson: true };
        }

        return null;
    }

    fullscreen(): void {
        if (!document.fullscreenElement) {
            this.jsonEditorWrapper.nativeElement
                .requestFullscreen()
                .then(() => {
                    this.isFullscreen$.next(true);
                })
                .catch((err: any) => {
                    alert(
                        `Error attempting to enable full-screen mode: ${err.message} (${err.name})`,
                    );
                });
        } else {
            this.isFullscreen$.next(false);
            document.exitFullscreen();
        }
    }
}
